import { Injectable } from "@angular/core";
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from "@angular/common/http";
import { defer, Observable, throwError } from "rxjs";
import { finalize, catchError } from "rxjs/operators";
import { AuthService } from "./auth.service";
import { HttpErrorResponse } from "@angular/common/http";
import { Router } from "@angular/router";
import { SharedService } from "../shared.service";
import { environment } from "src/environments/environment";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private authorize: AuthService, private router: Router, private sharedService: SharedService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const tracksLoader = !req.headers.get('no-loader');
    // withCredentials marks requests authorized with the impersonated tenant token
    // (see ApiHandlerService.GetTenantData) rather than the superadmin's own session.
    const isTenantRequest = req.withCredentials;
    let accessToken:string ='';
    if(!isTenantRequest){
      accessToken = this.authorize.getAccessToken()||'';
    }
    else{
      req = req.clone({
        withCredentials:false,
      });
      accessToken = this.authorize.getTenantAccessToken()||'';

    }
    // defer so increment fires at subscribe-time, paired with finalize on terminate.
    return defer(() => {
      if (tracksLoader) this.sharedService.incrementPending();
      return this.processRequestWithToken(accessToken as any, req, next);
    }).pipe(
      finalize(() => {
        if (tracksLoader) this.sharedService.decrementPending();
      }),
      catchError((error: HttpErrorResponse) => {
        let errorMsg = "";

        if (error.error instanceof ErrorEvent) {
          console.log("CLIENT Side Error");
          errorMsg = `Error: ${error.error.message}`;
        } else {
          // A handled API failure returns an ApiResponseDTO whose Response field carries the
          // operator-facing message; fall back to the generic line only when it is absent.
          const serverMessage = error.error && (error.error["Response"] || error.error["Error"]);
          errorMsg =
            typeof serverMessage === "string" && serverMessage.trim().length > 0
              ? serverMessage
              : `Error Code: ${error.status},  Message: ${error.message}, Possible Reason: Unknown`;
          if (error.status === 401) {
            if (isTenantRequest) {
              // The impersonated tenant token failed, not the superadmin session - don't log out.
              this.sharedService.showDialog("error", "Tenant data request was rejected (impersonation token invalid). Reopen the tenant to re-impersonate.", false);
            } else {
              this.router.navigate(["/auth/login"]);
              alert("Your session has been expired. Please sign in again.");
            }
          }
        }

        return throwError(errorMsg);
      })
    );
  }

  private processRequestWithToken(token: string, req: HttpRequest<any>, next: HttpHandler) {
    // First-party client attestation headers (mirrors the portal/mobile apps). The backend
    // BuildVersionActionFilter rejects authenticated requests that lack App-Version/Device-OS.
    // The superadmin app's own token has no CompanyId (so the gate skips it), but its
    // tenant/impersonation token DOES carry a CompanyId — without these headers those calls are
    // blocked as an "unsupported client". Device-OS "Web" maps to the (currently unset) web
    // minimum, so any version passes the floor; the headers' presence is what matters.
    const setHeaders: { [name: string]: string } = {
      "app-version": environment.appVersion,
      "Device-OS": "Web",
    };
    if (!!token) {
      setHeaders["Authorization"] = `Bearer ${token}`;
    }
    req = req.clone({ setHeaders });

    return next.handle(req);
  }
}
